/**
 * aminos-cart-telemetry.js
 *
 * Single dispatcher for analytics events tied to cart mutations.
 * Listens to `aminos:cart:settled` (fired by aminos-cart-client after the
 * authoritative server refresh) and fans out to Klaviyo / TripleWhale /
 * Gorgias / GA4 with consistent payload shape.
 *
 * Why one module: prevents race conditions where each app reads cart state
 * at a different moment in the mutation lifecycle. Telemetry fires AFTER
 * the cart has settled, not optimistically during.
 *
 * For Klaviyo: as of 2026, "Added to Cart" flows through Shopify's Server
 * Pixel (set up via the Klaviyo Shopify integration), NOT this client-side
 * dispatch. This module is for *additional* events we want to track —
 * specifically subscription toggle events that Klaviyo doesn't see otherwise.
 */
(function (global) {
  'use strict';

  // Keep last-known cart so we can diff before/after
  let lastCart = null;

  document.addEventListener('aminos:cart:settled', async (evt) => {
    const newCart = evt.detail && evt.detail.cart;
    if (!newCart) return;

    if (lastCart) {
      _detectSubscriptionToggle(lastCart, newCart);
    }
    lastCart = newCart;
  });

  function _detectSubscriptionToggle(prev, next) {
    // Match items by variant_id; report any that gained or lost a selling plan
    const prevByVariant = new Map(prev.items.map((i) => [i.variant_id, i]));

    next.items.forEach((nextItem) => {
      const prevItem = prevByVariant.get(nextItem.variant_id);
      if (!prevItem) return;

      const wasSub = prevItem.selling_plan_allocation != null;
      const isSub = nextItem.selling_plan_allocation != null;

      if (!wasSub && isSub) {
        _track('aminos:subscription_added', {
          product_id: nextItem.product_id,
          variant_id: nextItem.variant_id,
          variant_title: nextItem.variant_title,
          product_title: nextItem.product_title,
          selling_plan_id: nextItem.selling_plan_allocation.selling_plan.id,
          price: nextItem.price / 100,
          currency: next.currency,
        });
      } else if (wasSub && !isSub) {
        _track('aminos:subscription_removed', {
          product_id: nextItem.product_id,
          variant_id: nextItem.variant_id,
          variant_title: nextItem.variant_title,
          product_title: nextItem.product_title,
          price: nextItem.price / 100,
          currency: next.currency,
        });
      }
    });
  }

  function _track(eventName, payload) {
    // Klaviyo
    if (global._learnq) {
      global._learnq.push(['track', eventName, payload]);
    }

    // TripleWhale custom event
    if (global.TripleWhale && typeof global.TripleWhale.track === 'function') {
      global.TripleWhale.track(eventName, payload);
    }

    // GA4
    if (typeof global.gtag === 'function') {
      global.gtag('event', eventName.replace(/^aminos:/, ''), payload);
    }

    // Internal debug — Shopify Web Pixels custom event
    if (global.analytics && typeof global.analytics.publish === 'function') {
      try {
        global.analytics.publish(eventName, payload);
      } catch (_) { /* no-op */ }
    }
  }
})(window);
